import { Availability } from '../state/AppState';
import { processQueryParameters, fetchSessionData } from './sessionService';
import { processAvailableLocations } from './locationService';
import { isSelfPay } from '../utils/insuranceUtils';

export interface AvailabilityMatchResult {
  isMatch: boolean;
  locationMatch: boolean;
  regionMatch: boolean;
  serviceMatch: boolean;
  reasonMatch: boolean;
  insuranceMatch: boolean;
  timeMatch: boolean;
  dayMatch: boolean;
  debugInfo: {
    userLocationCode: string | null;
    availabilityLocationCode: string | null;
    availabilityLocationType: string | null;
    userStateString: string | null;
    availabilityRegion: string | null;
    userService: string | null;
    availabilityServices: string | null;
    userReason: string | null;
    availabilitySpecialties: string | null;
    userInsurance: string | null;
    availabilityInsurance: string | null;
    userTime: string | null;
    availabilityTime: string | null;
    userDate: string | null;
    availabilityStartDateIoi: string | null;
  };
}

/**
 * Deduplicate availability records.
 * 
 * Removes duplicate records based on contactId, startDate, startTime, and locationCode.
 * Keeps records that have different intervals (Weekly vs Bi-Weekly) as they represent
 * different scheduling patterns.
 * 
 * @param availabilities - Array of availability objects to deduplicate
 * @returns Deduplicated array of availability objects
 */
function deduplicateAvailabilities(availabilities: Availability[]): Availability[] {
  const seen = new Set<string>();
  const unique: Availability[] = [];
  
  for (const availability of availabilities) {
    // Create a unique key based on the core identifying fields
    const key = `${availability.contactId}-${availability.startDateIoi}-${availability.startTime}-${availability.locationCode}-${availability.region}`;
    
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(availability);
    }
  }
  
  return unique;
}

/**
 * Configuration for the availabilities service.
 * 
 * This interface defines the configuration options for fetching availabilities
 * from either remote APIs or static sample data.
 */
export interface AvailabilitiesConfig {
  /** Remote API base URL. If not provided, will use static sample data. */
  remoteApiUrl?: string;
  /** Provider ID for the API endpoint. */
  providerId?: string;
}

/**
 * Default configuration for the availabilities service.
 */
const DEFAULT_CONFIG: Required<AvailabilitiesConfig> = {
  remoteApiUrl: process.env.REACT_APP_REMOTE_API_URL || 'https://api-ioi-prod-westus2-001-hdddemdqdrgagybw.westus2-01.azurewebsites.net/',
  providerId: '' // No default - must come from contactId param or session
};

// Debug environment variable
/**
 * Fetch availabilities from remote API or static sample data.
 * 
 * This function will attempt to fetch availabilities from a remote API endpoint
 * if a remoteApiUrl is configured. If no remote URL is provided or the API call
 * fails, it will fall back to using static sample data.
 * 
 * The remote API endpoint format is: {remoteApiUrl}/api/IoiApi/providers/{providerId}/availabilities
 * 
 * @param config - Configuration options for fetching availabilities
 * @returns Promise that resolves to an array of Availability objects
 * 
 * @example
 * ```typescript
 * // Fetch from remote API
 * const availabilities = await fetchAvailabilities({
 *   remoteApiUrl: 'https://api.example.com',
 *   providerId: '123456789'
 * });
 * 
 * // Use static sample data
 * const availabilities = await fetchAvailabilities();
 * ```
 */
export async function fetchAvailabilities(
  config: AvailabilitiesConfig = {},
  stateRemoteApiUrl?: string | null,
  stateProviderId?: string | null
): Promise<Availability[]> {
  // Use state values if provided, otherwise fall back to config
  const remoteApiUrl = stateRemoteApiUrl || config.remoteApiUrl;
  const finalProviderId = stateProviderId || config.providerId;
  
  // Clear localStorage if it has the wrong providerId
  if (finalProviderId === '132') {
    localStorage.removeItem('app-config');
    // Reload the page to get fresh config
    window.location.reload();
  }
  
  // Require remote API URL
  if (!remoteApiUrl || remoteApiUrl.trim() === '') {
    throw new Error('Remote API URL is required. Please configure the API endpoint.');
  }
  
  try {
    const apiUrl = `${remoteApiUrl}/api/IoiApi/providers/${finalProviderId}/availabilities?errorIfEmpty=true`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Deduplicate the data before returning
    const deduplicatedData = deduplicateAvailabilities(data as Availability[]);
    
    return deduplicatedData;
    
  } catch (error) {
    throw new Error(`Failed to fetch availability data from ${remoteApiUrl}. Please ensure the API server is running and accessible.`);
  }
}

/**
 * Load availabilities and update application state.
 * 
 * This function fetches availabilities using the provided configuration and
 * dispatches the results to the application state. It handles both remote
 * API calls and fallback to static data.
 * 
 * @param config - Configuration options for fetching availabilities
 * @param dispatch - Redux-style dispatch function for updating state
 * @returns Promise that resolves when availabilities are loaded and state is updated
 * 
 * @example
 * ```typescript
 * const { dispatch } = useAppState();
 * 
 * await loadAvailabilities({
 *   remoteApiUrl: 'https://api.example.com'
 * }, dispatch);
 * ```
 */
export async function loadAvailabilities(
  config: AvailabilitiesConfig,
  dispatch: (action: any) => void,
  stateRemoteApiUrl?: string | null,
  stateProviderId?: string | null,
  currentState?: any
): Promise<void> {
  try {
    const baseUrl = stateRemoteApiUrl || config.remoteApiUrl || 'http://localhost:5293/';
    // First, fetch session data
    const sessionData = await fetchSessionData(baseUrl);
    dispatch({ type: 'SET_SESSION_DATA', payload: sessionData });
    
    // Process query parameters and get the resolved provider ID
    // This will handle mtgSession decryption and set the correct providerId
    const actualProviderId = await processQueryParameters(baseUrl, dispatch, sessionData);
    // Wait a moment for state to be updated after mtgSession processing
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Get the updated providerId from state after mtgSession processing
    // We need to get this from the state, not from the parameter
    const updatedProviderId = actualProviderId; // This should be the correct contactId from mtgSession
    // Now fetch availabilities with the correct providerId
    const availabilities = await fetchAvailabilities(config, stateRemoteApiUrl, updatedProviderId);
    
    // Process available locations and save to state
    let availableLocations: any[] = [];
    if (availabilities && availabilities.length > 0) {
      // Get selected region from current state (which includes continue response data)
      const selectedRegion = currentState?.stateString || null;
      // Get the actual region from availability data for validation
      const regionSet = new Set(availabilities.map(a => a.region));
      const actualRegions = Array.from(regionSet);
      // Prioritize continue response region, but validate it exists in availability data
      let regionToUse = selectedRegion;
      
      if (selectedRegion && actualRegions.includes(selectedRegion)) {
        regionToUse = selectedRegion;
      } else if (actualRegions.length > 0) {
        regionToUse = actualRegions[0];
        // Update state with the correct region
        dispatch({ type: 'SET_STATE_STRING', payload: regionToUse });
      } else {
        regionToUse = selectedRegion;
      }
      if (regionToUse) {
        // Process all available locations
        const processStartTime = Date.now();
        availableLocations = await processAvailableLocations({
          availabilities,
          selectedRegion: regionToUse,
          includeTelehealth: true
        });
        const processEndTime = Date.now();
        
        
        // Auto-populate location data based on session data location
        let sessionLocation: string | null = null;
        try {
          if (sessionData?.data?.data) {
            const parsedData = JSON.parse(sessionData.data.data);
            sessionLocation = parsedData.location || null;
          }
        } catch (error) {
        }
        
        if (sessionLocation && availableLocations.length > 0) {
          // Find matching location by name
          const matchingLocation = availableLocations.find(loc => 
            loc.name === sessionLocation || 
            (sessionLocation && loc.name.toLowerCase() === sessionLocation.toLowerCase())
          );
          
          if (matchingLocation) {
            // Set location data as if the button was clicked
            dispatch({ type: 'SET_LOCATION', payload: matchingLocation.isTelehealth ? 'Telehealth' : matchingLocation.name });
            dispatch({ type: 'SET_LOCATION_CODE', payload: matchingLocation.isTelehealth ? 'telehealth' : matchingLocation.id });
            dispatch({ type: 'SET_LOCATION_ADDRESS', payload: matchingLocation.isTelehealth ? '' : (matchingLocation.address || '') });
          } else {
            
          }
        }
      } else {
      }
    } else {
    }
    dispatch({ type: 'SET_AVAILABILITIES', payload: availabilities });
    
    dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: availableLocations });
  } catch (error) {
    // Still dispatch empty arrays to indicate loading is complete
    dispatch({ type: 'SET_AVAILABILITIES', payload: [] });
    dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: [] });
    dispatch({ type: 'SET_SESSION_DATA', payload: null });
  }
}

/**
 * Get availabilities by provider code.
 * 
 * Filters the provided availabilities array to return only those
 * matching the specified provider code.
 * 
 * @param availabilities - Array of availability objects
 * @param providerCode - Provider code to filter by
 * @returns Filtered array of availabilities for the specified provider
 * 
 * @example
 * ```typescript
 * const providerAvailabilities = getAvailabilitiesByProvider(availabilities, '60032');
 * ```
 */
export function getAvailabilitiesByProvider(
  availabilities: Availability[],
  providerCode: string
): Availability[] {
  return availabilities.filter(availability => availability.providerCode === providerCode);
}

/**
 * Get availabilities by location.
 * 
 * Filters the provided availabilities array to return only those
 * matching the specified location code.
 * 
 * @param availabilities - Array of availability objects
 * @param locationCode - Location code to filter by
 * @returns Filtered array of availabilities for the specified location
 * 
 * @example
 * ```typescript
 * const locationAvailabilities = getAvailabilitiesByLocation(availabilities, '1999');
 * ```
 */
export function getAvailabilitiesByLocation(
  availabilities: Availability[],
  locationCode: string
): Availability[] {
  return availabilities.filter(availability => availability.locationCode === locationCode);
}

/**
 * Get availabilities by day of week.
 * 
 * Filters the provided availabilities array to return only those
 * matching the specified day of week.
 * 
 * @param availabilities - Array of availability objects
 * @param dayOfWeek - Day of week to filter by (e.g., 'Monday', 'Tuesday')
 * @returns Filtered array of availabilities for the specified day
 * 
 * @example
 * ```typescript
 * const mondayAvailabilities = getAvailabilitiesByDay(availabilities, 'Monday');
 * ```
 */
export function getAvailabilitiesByDay(
  availabilities: Availability[],
  dayOfWeek: string
): Availability[] {
  return availabilities.filter(availability => availability.dayOfWeek === dayOfWeek);
}

/**
 * Get availabilities filtered by region and location for appointment scheduling.
 * 
 * Filters availabilities based on selected region and location, handling
 * both physical locations and telehealth appointments.
 * 
 * @param availabilities - Array of availability objects
 * @param selectedRegion - Selected region/state (e.g., "Washington")
 * @param selectedLocation - Selected location name or code
 * @returns Filtered array of availabilities matching the criteria
 * 
 * @example
 * ```typescript
 * const filteredAvailabilities = getAvailabilitiesForScheduling(availabilities, 'Washington', 'Northgate Office');
 * ```
 */
export function getAvailabilitiesForScheduling(
  availabilities: Availability[],
  selectedRegion: string | null,
  selectedLocation: string | null
): Availability[] {
  const filtered = availabilities.filter(availability => {
    // Filter by region
    const regionMatch = !selectedRegion || 
      availability.region === selectedRegion;
    
    // Filter by location if specified - use locationCode for matching
    const locationMatch = !selectedLocation || 
      availability.locationCode === selectedLocation ||
      // Special case for telehealth
      (selectedLocation === 'telehealth' && availability.locationType === 'Telehealth');
    
    return regionMatch && locationMatch;
  });
  
  return filtered;
}

/**
 * Match a single availability against user selections
 */
export function matchAvailability(
  availability: Availability,
  userSelections: {
    locationCode: string | null;
    stateString: string | null;
    service: string | null;
    reason: string | null;
    insurance: string | null;
    appointmentTime: string | null;
    appointmentDate: string | null;
  }
): AvailabilityMatchResult {
  // Calculate all 7 matching criteria for debugging purposes
  // But only use region and time for actual filtering (to maximize matches)
  
  // Match by location
  const locationMatch = !userSelections.locationCode || 
                       availability.locationCode === userSelections.locationCode ||
                       (userSelections.locationCode === 'telehealth' && availability.locationType === 'Telehealth');
  
  // Match by region/state
  const regionMatch = !userSelections.stateString || availability.region === userSelections.stateString;
  
  // Match by service (if available in availability data)
  const serviceMatch = !userSelections.service || 
                      !availability.providerServices || 
                      availability.providerServices.includes(userSelections.service);
  
  // Match by reason/specialty (if available in availability data)
  const reasonMatch = !userSelections.reason || 
                     !availability.specialties || 
                     availability.specialties.includes(userSelections.reason);
  
  // Match by insurance (if available in availability data)
  // Skip insurance filter if user selected self-pay
  const isSelfPaySelection = isSelfPay(userSelections.insurance);
  const insuranceMatch = isSelfPaySelection || 
                        !userSelections.insurance || 
                        !availability.insuranceInNetwork || 
                        availability.insuranceInNetwork.includes(userSelections.insurance);
  
  // Match by appointment time (if specified)
  const timeMatch = !userSelections.appointmentTime || 
                   (() => {
                     if (!availability.startTime) return false;
                     
                     // Extract hour from user time "3:30pm - 4:30pm" -> "3pm" or "3pm - 4pm" -> "3pm"
                     // Match hour (1-2 digits) followed by optional colon and minutes, then am/pm
                     const userTimeMatch = userSelections.appointmentTime.match(/(\d{1,2})(?::\d{2})?(am|pm)/i);
                     if (!userTimeMatch) return false;
                     
                     const userHour = parseInt(userTimeMatch[1]);
                     const userPeriod = userTimeMatch[2].toLowerCase();
                     
                     // Convert to 24-hour format
                     let userHour24 = userHour;
                     if (userPeriod === 'pm' && userHour !== 12) userHour24 += 12;
                     if (userPeriod === 'am' && userHour === 12) userHour24 = 0;
                     
                     // Extract hour from availability time "15:30:00" -> 15
                     const availHour = parseInt(availability.startTime.split(':')[0]);
                     
                     return userHour24 === availHour;
                   })();

  // Match by appointment date (if specified)
  const dayMatch = !userSelections.appointmentDate || 
                  !availability.startDateIoi || 
                  availability.startDateIoi.startsWith(userSelections.appointmentDate);

  // REDUCED FILTER: Only use region and time for actual matching
  // All other criteria are calculated above for debugging but not used in isMatch
  const isMatch = regionMatch && timeMatch;
  
  return {
    isMatch,
    locationMatch,
    regionMatch,
    serviceMatch,
    reasonMatch,
    insuranceMatch,
    timeMatch,
    dayMatch,
    debugInfo: {
      userLocationCode: userSelections.locationCode,
      availabilityLocationCode: availability.locationCode,
      availabilityLocationType: availability.locationType,
      userStateString: userSelections.stateString,
      availabilityRegion: availability.region,
      userService: userSelections.service,
      availabilityServices: availability.providerServices,
      userReason: userSelections.reason,
      availabilitySpecialties: availability.specialties,
      userInsurance: userSelections.insurance,
      availabilityInsurance: availability.insuranceInNetwork,
      userTime: userSelections.appointmentTime,
      availabilityTime: availability.startTime,
      userDate: userSelections.appointmentDate,
      availabilityStartDateIoi: availability.startDateIoi
    }
  };
}

/**
 * Find matching and non-matching availabilities based on user selections
 */
export function findMatchingAvailabilities(
  availabilities: Availability[],
  userSelections: {
    locationCode: string | null;
    stateString: string | null;
    service: string | null;
    reason: string | null;
    insurance: string | null;
    appointmentTime: string | null;
    appointmentDate: string | null;
  }
): { matching: Availability[]; nonMatching: Availability[] } {
  const matching: Availability[] = [];
  const nonMatching: Availability[] = [];
  
  availabilities.forEach(availability => {
    const matchResult = matchAvailability(availability, userSelections);
    
    if (matchResult.isMatch) {
      matching.push(availability);
    } else {
      nonMatching.push(availability);
    }
  });
  
  return { matching, nonMatching };
}
